import { useState, useEffect, useLayoutEffect, useRef } from 'react'

/**
 * Ligne de pastilles (notions + difficulté) avec affichage dynamique :
 * autant de pastilles que la largeur permet + pastille "+X" si besoin.
 * Partagé entre ExerciseCard (élève) et DashboardCardTooltipZone (prof).
 * La mesure du conteneur (ResizeObserver) assure l’adaptation à toute taille de carte.
 */
function NotionPillsRow({ items, containerClassName, moreClassName, renderPill, leading }) {
  const [visiblePillCount, setVisiblePillCount] = useState(items.length)
  const rowRef = useRef(null)
  const visiblePillCountRef = useRef(visiblePillCount)
  const itemsLengthRef = useRef(items.length)
  const lastClientWidthWhenDecrementedRef = useRef(0)
  const runOverflowCheckRef = useRef(null)

  visiblePillCountRef.current = visiblePillCount
  itemsLengthRef.current = items.length

  useEffect(() => {
    setVisiblePillCount(items.length)
  }, [items.length])

  useEffect(() => {
    const el = rowRef.current
    if (!el) return
    const check = () => {
      const count = visiblePillCountRef.current
      const len = itemsLengthRef.current
      const sw = el.scrollWidth
      const cw = el.clientWidth
      const overflow = sw > cw
      const containerGrew = cw > lastClientWidthWhenDecrementedRef.current
      if (overflow && count > 0) {
        lastClientWidthWhenDecrementedRef.current = cw
        setVisiblePillCount((c) => c - 1)
      } else if (containerGrew && count < len) {
        setVisiblePillCount(len)
      }
    }
    runOverflowCheckRef.current = check
    check()
    const ro = new ResizeObserver(check)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  useLayoutEffect(() => {
    if (runOverflowCheckRef.current) runOverflowCheckRef.current()
  }, [visiblePillCount])

  const visibleItems = items.slice(0, visiblePillCount)
  const overflowCount = items.length - visiblePillCount

  return (
    <div ref={rowRef} className={containerClassName}>
      {leading}
      {visibleItems.map((item, i) => renderPill(item, i))}
      {overflowCount > 0 && (
        <span
          className={moreClassName}
          aria-label={`${overflowCount} notion${overflowCount > 1 ? 's' : ''} en plus`}
        >
          +{overflowCount}
        </span>
      )}
    </div>
  )
}

export default NotionPillsRow
